/**
 * RxNorm API Integration Utility
 * Provides functions to search medications using the National Library of Medicine's RxNorm API
 * Documentation: https://rxnav.nlm.nih.gov/RxNavAPIs.html
 */

import { resolveMedicationRoutes, debouncedResolveRoutes } from './enhancedRxnormResolver';

const RXNAV_BASE_URL = 'https://rxnav.nlm.nih.gov/REST';

/**
 * Search for medications by name using RxNorm API
 * @param {string} query - Medication name to search for
 * @param {number} maxResults - Maximum number of results to return (default: 20)
 * @returns {Promise<Array>} Array of medication objects with name, rxcui, and synonyms
 */
export const searchMedications = async (query, maxResults = 20) => {
  if (!query || query.trim().length < 1) {
    return [];
  }

  const term = query.trim();

  const normalizeResults = (conceptGroups = []) => {
    const allConcepts = conceptGroups
      .flatMap((g) => g?.concept || [])
      .filter(Boolean);

    const meds = allConcepts
      .map((concept) => ({
        name: concept.name,
        rxcui: concept.rxcui,
        tty: concept.tty,
        synonym: concept.synonym || concept.name,
      }))
      .filter((m) => m.name && m.rxcui);

    // Deduplicate by name+rxcui
    const seen = new Set();
    const deduped = [];
    for (const m of meds) {
      const key = `${m.name}|${m.rxcui}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(m);
      }
    }
    return deduped;
  };

  try {
    // 1) Primary: exact/contains search
    const primaryRes = await fetch(`${RXNAV_BASE_URL}/drugs.json?name=${encodeURIComponent(term)}`);
    if (primaryRes.ok) {
      const primaryData = await primaryRes.json();
      let meds = normalizeResults(primaryData?.drugGroup?.conceptGroup || []);
      if (meds.length >= 1) return meds.slice(0, maxResults);
    }

    // 2) Spelling suggestions
    const spellRes = await fetch(`${RXNAV_BASE_URL}/spellingsuggestions.json?name=${encodeURIComponent(term)}`);
    if (spellRes.ok) {
      const spellData = await spellRes.json();
      const suggestion = spellData?.suggestionGroup?.suggestionList?.suggestion?.[0];
      if (suggestion) {
        const sugRes = await fetch(`${RXNAV_BASE_URL}/drugs.json?name=${encodeURIComponent(suggestion)}`);
        if (sugRes.ok) {
          const sugData = await sugRes.json();
          const meds = normalizeResults(sugData?.drugGroup?.conceptGroup || []);
          if (meds.length >= 1) return meds.slice(0, maxResults);
        }
      }
    }

    // 3) Approximate (fuzzy) term match to get RXCUIs, then resolve to names
    const approxRes = await fetch(`${RXNAV_BASE_URL}/approximateTerm.json?term=${encodeURIComponent(term)}&maxEntries=20`);
    if (approxRes.ok) {
      const approxData = await approxRes.json();
      const candidates = approxData?.approximateGroup?.candidate || [];
      const rxcuis = [...new Set(candidates.map((c) => c?.rxcui).filter(Boolean))].slice(0, maxResults);
      const resolved = [];
      for (const rxcui of rxcuis) {
        try {
          const propsRes = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}/properties.json`);
          if (!propsRes.ok) continue;
          const props = await propsRes.json();
          const p = props?.properties;
          if (p?.name && p?.rxcui) {
            resolved.push({ name: p.name, rxcui: p.rxcui, tty: p.tty, synonym: p.name });
          }
        } catch (e) {}
      }
      if (resolved.length) return resolved.slice(0, maxResults);
    }

    return [];
  } catch (error) {
    console.error('RxNorm API search error:', error);
    return [];
  }
};

/**
 * Get detailed information about a specific medication by RxCUI
 * @param {string} rxcui - RxCUI identifier for the medication
 * @returns {Promise<Object|null>} Detailed medication information or null if not found
 */
export const getMedicationDetails = async (rxcui) => {
  if (!rxcui) {
    return null;
  }

  try {
    const response = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}/properties.json`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.properties || null;
  } catch (error) {
    console.error('RxNorm API details error:', error);
    return null;
  }
};

/**
 * Get drug interactions for a medication
 * @param {string} rxcui - RxCUI identifier for the medication
 * @returns {Promise<Array>} Array of drug interactions
 */
export const getDrugInteractions = async (rxcui) => {
  if (!rxcui) {
    return [];
  }

  try {
    const response = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}/interactions.json`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.interactionTypeGroup?.[0]?.interactionType || [];
  } catch (error) {
    console.error('RxNorm API interactions error:', error);
    return [];
  }
};

/**
 * Get available dosage forms for a medication
 * @param {string} rxcui - RxCUI identifier for the medication
 * @returns {Promise<Array>} Array of available dosage forms
 */
export const getDosageForms = async (rxcui) => {
  if (!rxcui) {
    return [];
  }

  try {
    const response = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}/related.json?tty=SCD`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.relatedGroup?.conceptGroup?.[0]?.concept || [];
  } catch (error) {
    console.error('RxNorm API dosage forms error:', error);
    return [];
  }
};

/**
 * Get medication strength information
 * @param {string} rxcui - RxCUI identifier for the medication
 * @returns {Promise<Array>} Array of available strengths
 */
export const getMedicationStrengths = async (rxcui) => {
  if (!rxcui) {
    return [];
  }

  try {
    const response = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}/related.json?tty=SCD`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const concepts = data.relatedGroup?.conceptGroup?.[0]?.concept || [];
    
    // Extract strengths from concept names
    const strengths = concepts
      .map(concept => {
        const name = concept.name || '';
        // Look for strength patterns like "100mg", "50 mg", "10mg tablet", etc.
        const strengthMatch = name.match(/(\d+(?:\.\d+)?)\s*(mg|mcg|g|IU|units?)/i);
        if (strengthMatch) {
          return `${strengthMatch[1]} ${strengthMatch[2].toLowerCase()}`;
        }
        return null;
      })
      .filter(strength => strength !== null)
      .filter((strength, index, array) => array.indexOf(strength) === index); // Remove duplicates
    
    return strengths;
  } catch (error) {
    console.error('RxNorm API strengths error:', error);
    return [];
  }
};

/**
 * Get available administration routes for a medication from RxNorm
 * Strategy:
 * 1) Query allProperties with prop=ATTR and extract ROUTE values
 * 2) Fallback: parse route texts from related SCD concept names
 * @param {string} rxcui
 * @returns {Promise<Array<string>>}
 */

/**
 * Enhanced medication route resolver using algorithmic approach
 * @param {string} medicationName - The medication name to resolve routes for
 * @returns {Promise<Array<string>>} Array of available routes
 */
export const getMedicationRoutes = async (medicationName) => {
  if (!medicationName) return [];
  
  try {
    return await resolveMedicationRoutes(medicationName);
  } catch (e) {
    console.error('Enhanced RxNorm routes error:', e);
    return [];
  }
};

/**
 * Legacy function for backward compatibility - converts RxCUI to medication name first
 * @param {string} rxcui - RxNorm concept unique identifier
 * @returns {Promise<Array<string>>} Array of available routes
 */
export const getMedicationRoutesByRxcui = async (rxcui) => {
  if (!rxcui) return [];
  
  try {
    // Get medication name from RxCUI
    const response = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}.json`);
    if (!response.ok) return [];
    
    const data = await response.json();
    const medicationName = data?.idGroup?.name;
    
    if (!medicationName) return [];
    
    // Use enhanced resolver
    return await resolveMedicationRoutes(medicationName);
  } catch (e) {
    console.error('RxNorm routes by RxCUI error:', e);
    return [];
  }
};

/**
 * Debounced route resolution for frontend use
 * @param {string} medicationName - The medication name
 * @param {Function} callback - Callback function to receive routes
 * @param {number} delay - Debounce delay in milliseconds (default: 300)
 */
export const getMedicationRoutesDebounced = debouncedResolveRoutes;

/**
 * Search for medications with debouncing to avoid excessive API calls
 * @param {string} query - Search query
 * @param {number} delay - Debounce delay in milliseconds (default: 300)
 * @param {number} maxResults - Maximum results to return
 * @returns {Promise<Array>} Array of medication search results
 */
export const debouncedSearchMedications = (() => {
  let timeoutId;
  
  return (query, delay = 300, maxResults = 20) => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const results = await searchMedications(query, maxResults);
        resolve(results);
      }, delay);
    });
  };
})();

/**
 * Validate if a medication name exists in RxNorm
 * @param {string} medicationName - Name of the medication to validate
 * @returns {Promise<boolean>} True if medication exists in RxNorm, false otherwise
 */
export const validateMedicationName = async (medicationName) => {
  if (!medicationName || medicationName.trim().length < 1) {
    return false;
  }

  try {
    const results = await searchMedications(medicationName, 1);
    return results.length > 0 && results[0].name.toLowerCase() === medicationName.toLowerCase();
  } catch (error) {
    console.error('Medication validation error:', error);
    return false;
  }
};

/**
 * Get medication suggestions for autocomplete
 * @param {string} query - Partial medication name
 * @param {number} maxResults - Maximum number of suggestions
 * @returns {Promise<Array>} Array of medication suggestions
 */
export const getMedicationSuggestions = async (query, maxResults = 20) => {
  if (!query || query.trim().length < 1) {
    return [];
  }

  try {
    const term = query.trim().toLowerCase();
    // Ask for more results, we'll filter/dedupe client-side
    const results = await searchMedications(query, Math.max(50, maxResults));

    if (!Array.isArray(results)) {
      console.warn('RxNorm API returned non-array results:', results);
      return [];
    }

    // Map to primary names and dedupe by the displayed label
    const uniqueByLabel = new Map();
    for (const med of results) {
      if (!med || !med.name) continue;
      const primaryName = extractPrimaryMedicationName(med.name);
      if (!primaryName) continue;
      const key = primaryName.toLowerCase();
      // Only keep one entry per primary name (first occurrence wins)
      if (!uniqueByLabel.has(key)) {
        uniqueByLabel.set(key, {
          label: primaryName,
          value: primaryName,
          rxcui: med.rxcui,
          tty: med.tty,
          synonym: med.synonym,
          fullName: med.name
        });
      }
    }

    // Convert to array and filter by the typed term anywhere in the string
    let suggestions = Array.from(uniqueByLabel.values()).filter(s =>
      s.label.toLowerCase().includes(term)
    );

    // Sort to prioritize names that start with the query, then alphabetical
    suggestions.sort((a, b) => {
      const aStarts = a.label.toLowerCase().startsWith(term) ? 0 : 1;
      const bStarts = b.label.toLowerCase().startsWith(term) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return a.label.localeCompare(b.label);
    });

    return suggestions.slice(0, maxResults);
  } catch (error) {
    console.error('Medication suggestions error:', error);
    console.error('Error details:', error?.message || error?.toString() || 'Unknown error');
    return [];
  }
};

/**
 * Extract primary medication name from full RxNorm name
 * Examples:
 * - "Prometrium 100mg Capsule" → "Prometrium"
 * - "Progesterone 100mg Oral Capsule" → "Progesterone"
 * - "Clomiphene Citrate 50mg Tablet" → "Clomiphene Citrate"
 * - "Letrozole 2.5mg Tablet" → "Letrozole"
 */
const extractPrimaryMedicationName = (fullName) => {
  if (!fullName) return '';
  
  // Common patterns to remove: strength, dosage form, route
  const patterns = [
    /\s+\d+\.?\d*\s*(mg|mcg|g|IU|units?)\s*/gi, // Remove strength (e.g., "100mg", "2.5mg")
    /\s+(tablet|capsule|vial|pen|syringe|injection|gel|cream|patch|spray)\s*/gi, // Remove dosage forms
    /\s+(oral|topical|injection|subcutaneous|intramuscular|intravenous)\s*/gi, // Remove routes
    /\s+(daily|bid|tid|qid|weekly|monthly)\s*/gi, // Remove frequency
    /\s+(depot|extended\s+release|sustained\s+release|er|sr)\s*/gi, // Remove release forms
    /\s+(hcl|citrate|sodium|potassium|acetate|succinate)\s*/gi, // Remove salt forms
    /\s+\([^)]*\)\s*/g, // Remove parenthetical info
    /\s+-\s+.*$/g, // Remove everything after dash
    /\s+\d+.*$/g // Remove everything after first number
  ];
  
  let primaryName = fullName;
  
  // Apply patterns to clean up the name
  patterns.forEach(pattern => {
    primaryName = primaryName.replace(pattern, ' ');
  });
  
  // Clean up extra spaces and trim
  primaryName = primaryName.replace(/\s+/g, ' ').trim();
  
  // Special handling for common medication patterns
  const words = primaryName.split(' ');
  
  // For single word medications, return as is
  if (words.length === 1) {
    return primaryName;
  }
  
  // For 2-3 words, usually good as is
  if (words.length <= 3) {
    return primaryName;
  }
  
  // For longer names, take first 2 words (drug name + salt if needed)
  // But avoid common non-drug words
  const commonNonDrugWords = ['extended', 'release', 'sustained', 'delayed', 'immediate'];
  const filteredWords = words.filter(word => !commonNonDrugWords.includes(word.toLowerCase()));
  
  if (filteredWords.length >= 2) {
    return filteredWords.slice(0, 2).join(' ');
  }
  
  return words.slice(0, 2).join(' ');
};

const rxnormApi = {
  searchMedications,
  getMedicationDetails,
  getDrugInteractions,
  getDosageForms,
  getMedicationStrengths,
  debouncedSearchMedications,
  validateMedicationName,
  getMedicationSuggestions
};

export default rxnormApi;
